import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import "../css/dash.css"; // ← we use your dashboard styles
import getSubscriptionStatus from "../backend/getsub";

export default function Analytics() {
  const [user, setUser] = useState(null);
  const [menus, setMenus] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        nav("/");
        return;
      }
      
      const userRef = doc(db, "users", currentUser.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        setUser(snap.data());
        loadMenus(snap.data());
      }
    });
    return () => unsub();
  }, []);

  const loadMenus = async (userData) => {
    const subStatus = await getSubscriptionStatus()
    if(!subStatus.active){
        nav("/checkout")
    }
    if (!userData?.menus?.length) return;
    const menuDocs = await Promise.all(
      userData.menus.map(async (menuId) => {
        const ref = doc(db, "menus", menuId);
        const snap = await getDoc(ref);
        return snap.exists() ? { id: menuId, ...snap.data() } : null;
      })
    );

    setMenus(menuDocs.filter(Boolean));
  };

  const COLORS = ["#2e7d32", "#1976d2", "#ff9800", "#9c27b0", "#f44336", "#009688"];

  if (!user) {
    return (
      <div className="loadingContainer">
        <div className="loader"></div>
        <h2>Loading analytics...</h2>
      </div>
    );
  }

  const chartData = menus.map((m) => ({
    name: m.name,
    views: m.views || 0,
  }));

  const totalViews = chartData.reduce((s, m) => s + m.views, 0);

  return (
    <section className="dashboard">
      {/* Sidebar */}
      <aside className={`sidebar ${menuOpen ? "open" : ""}`}>
        <h3 className="closeSideBar" onClick={() => setMenuOpen(false)}>X</h3>
        <h2 className="logo">MenuLinker</h2>

        <nav>
          <ul>
            <li onClick={() => nav("/dash")}>Dashboard</li>
            <li className="active">Analytics</li>
            <li onClick={() => nav("/settings")}>Settings</li>
            <li><a href="mailto:tglewis247@gmail.com">Contact</a></li>
          </ul>
        </nav>

        <button
          className="logoutBtn"
          onClick={() => {
            auth.signOut();
            nav("/");
          }}
        >
          Log out
        </button>
      </aside>

      {/* Main content */}
      <main className="mainContent">
        <header className="dashHeader">
          <button className="menuToggle" onClick={() => setMenuOpen(!menuOpen)}>
            ☰
          </button>

          <div>
            <h1>Analytics 📊</h1>
            <p>Overview of your menu performance</p>
          </div>
        </header>

        <section className="cardsGrid">
          {/* Summary Card */}
          <div className="card">
            <h3>Summary</h3>
            <p>Total Menus: <strong>{menus.length}</strong></p>
            <p>Total Views: <strong>{totalViews.toLocaleString()}</strong></p>
            <p>Avg Views per Menu: <strong>{(totalViews / menus.length || 0).toFixed(0)}</strong></p>
          </div>

          {/* Bar Chart */}
          <div className="card">
            <h3>Views per Menu</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="views">
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart */}
          <div className="card">
            <h3>Views Distribution</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="views"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </section>
      </main>
    </section>
  );
}
